import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useOrgStore } from '@/stores/useOrgStore';
import { usePortfolioStore } from '@/stores/usePortfolioStore';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatNumber, formatDate } from '@/lib/format';
import { FolderOpen, Plus } from 'lucide-react';

const Portfolios = () => {
  const navigate = useNavigate();
  const { activeOrg } = useOrgStore();
  const { portfolios, setPortfolios } = usePortfolioStore();

  useEffect(() => {
    if (!activeOrg) return;

    const loadPortfolios = async () => {
      // TODO: Load via edge function
      setPortfolios([]);
    };

    loadPortfolios();
  }, [activeOrg, setPortfolios]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-500',
      valuated: 'bg-blue-500',
      active: 'bg-green-500',
      listed: 'bg-yellow-500',
      closed: 'bg-red-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Portfolios</h1>
            <p className="text-muted-foreground">
              Manage your debt portfolio collection
            </p>
          </div>
          <Button onClick={() => navigate('/upload')}>
            <Plus className="mr-2 h-4 w-4" />
            Upload Portfolio
          </Button>
        </div>

        {portfolios.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                No Portfolios Yet
              </CardTitle>
              <CardDescription>
                Get started by uploading your first debt portfolio using the button above
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {portfolios.map((portfolio) => (
              <Card
                key={portfolio.id}
                className="cursor-pointer transition-all hover:border-primary"
                onClick={() => navigate(`/portfolios/${portfolio.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{portfolio.name}</CardTitle>
                    <Badge className={getStatusColor(portfolio.status)}>
                      {portfolio.status}
                    </Badge>
                  </div>
                  <CardDescription>
                    Created {formatDate(portfolio.created_at)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Nominal Value</span>
                    <span className="font-semibold">
                      {formatCurrency(portfolio.nominal_value)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Accounts</span>
                    <span className="font-semibold">
                      {formatNumber(portfolio.account_count)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default Portfolios;
